import { getSupabase } from "./supabase";
import type { OAuthProvider } from "./providers";

/** The email has no account and this path may not create one (the sign-in page). */
export class NoAccountError extends Error {
  constructor() {
    super("no account for this email");
    this.name = "NoAccountError";
  }
}

/** Supabase's per-address email cooldown refused a new code (D10): an invite or code went
 * to this address moments ago. */
export class CodeRateLimitedError extends Error {
  constructor() {
    super("an email was just sent to this address");
    this.name = "CodeRateLimitedError";
  }
}

/** Emails a one-time code. Sign-up creates the account; sign-in never does. */
export async function requestEmailCode(email: string, createUser: boolean): Promise<void> {
  const { error } = await getSupabase().auth.signInWithOtp({
    email,
    options: { shouldCreateUser: createUser },
  });
  if (!error) return;
  if (!createUser && (error.code === "otp_disabled" || /signups? not allowed/i.test(error.message))) {
    throw new NoAccountError();
  }
  if (error.code === "over_email_send_rate_limit" || error.status === 429) throw new CodeRateLimitedError();
  throw error;
}

export async function verifyEmailCode(email: string, code: string): Promise<void> {
  const { error } = await getSupabase().auth.verifyOtp({ email, token: code, type: "email" });
  if (error) throw error;
}

/** Redirects to the provider. Supabase links identities on the same verified email, so one
 * email stays one account (R12, R13). Azure needs the `email` scope to return it. */
export async function signInWithProvider(provider: OAuthProvider, redirectTo: string): Promise<void> {
  const { error } = await getSupabase().auth.signInWithOAuth({
    provider,
    options: { redirectTo, ...(provider === "azure" ? { scopes: "email" } : {}) },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await getSupabase().auth.signOut();
}
